'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp, CircleCheck, CircleX } from 'lucide-react'

interface QueryResult {
  query: string
  queryType: 'brand' | 'competitive' | 'reputation'
  brandMentioned: boolean
  urlCited: boolean
  sentiment: 'positive' | 'neutral' | 'negative' | 'not_mentioned'
  prominence: 'primary' | 'mentioned' | 'listed' | 'absent'
  snippet: string
}

interface ProviderResult {
  provider: string
  available: boolean
  queries: QueryResult[]
}

interface QueryMatrixProps {
  providers: ProviderResult[]
}

const PROVIDER_COLORS: Record<string, string> = {
  ChatGPT: '#10a37f',
  Perplexity: '#20808d',
  Claude: '#cc9b7a',
  Gemini: '#4285f4',
}

const QUERY_TYPE_COLORS: Record<string, string> = {
  brand: '#008dcc',
  competitive: '#8b5cf6',
  reputation: '#FDC304',
}

export default function QueryMatrix({ providers }: QueryMatrixProps) {
  const [expanded, setExpanded] = useState(true)
  const activeProviders = providers.filter(p => p.available)

  if (activeProviders.length === 0) return null

  // Get all unique queries from first active provider
  const queries = activeProviders[0]?.queries || []

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <button
        onClick={() => setExpanded(!expanded)}
        className='flex items-center justify-between w-full text-left'
      >
        <h2 className='text-lg font-semibold text-foreground'>
          Query x Provider Matrix
        </h2>
        <span className='flex items-center gap-1 text-xs text-muted-foreground'>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      {expanded && (
        <div className='mt-4 overflow-x-auto'>
          <table className='w-full text-xs'>
            <thead>
              <tr>
                <th className='text-left py-2 pr-4 text-muted-foreground font-medium'>
                  Query
                </th>
                {activeProviders.map(p => (
                  <th
                    key={p.provider}
                    className='text-center py-2 px-3 font-semibold'
                    style={{ color: PROVIDER_COLORS[p.provider] }}
                  >
                    {p.provider}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queries.map((q, qi) => (
                <tr key={qi} className='border-t border-border/40'>
                  <td className='py-2.5 pr-4'>
                    <div className='flex items-center gap-2'>
                      <span
                        className='text-[10px] font-medium rounded-full px-1.5 py-0 shrink-0'
                        style={{
                          backgroundColor: `${QUERY_TYPE_COLORS[q.queryType]}15`,
                          color: QUERY_TYPE_COLORS[q.queryType],
                        }}
                      >
                        {q.queryType}
                      </span>
                      <span className='text-foreground/80'>{q.query}</span>
                    </div>
                  </td>
                  {activeProviders.map(p => {
                    const pQuery = p.queries[qi]
                    if (!pQuery) {
                      return (
                        <td
                          key={p.provider}
                          className='text-center py-2.5 px-3'
                        >
                          <span className='text-muted-foreground'>-</span>
                        </td>
                      )
                    }
                    return (
                      <td key={p.provider} className='text-center py-2.5 px-3'>
                        {pQuery.brandMentioned ? (
                          <div className='flex flex-col items-center gap-0.5'>
                            <CircleCheck
                              size={16}
                              style={{ color: '#22c55e' }}
                            />
                            {pQuery.urlCited && (
                              <span
                                className='text-[9px] font-medium'
                                style={{
                                  color: PROVIDER_COLORS[p.provider],
                                }}
                              >
                                URL
                              </span>
                            )}
                          </div>
                        ) : (
                          <CircleX size={16} style={{ color: '#F90B8A40' }} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
